/**
 * Orders Hook
 *
 * Fetches user orders for account page.
 */

import { useState, useEffect, useCallback } from 'react';
import { accountApi } from '@/api/client';
import { useAuth } from '@/context';
import type { Order } from '@/types';

interface UseOrdersOptions {
  page?: number;
  perPage?: number;
}

interface UseOrdersResult {
  orders: Order[];
  total: number;
  pages: number;
  currentPage: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  goToPage: (page: number) => void;
}

export function useOrders(options: UseOrdersOptions = {}): UseOrdersResult {
  const { page = 1, perPage = 10 } = options;
  const { isLoggedIn } = useAuth();

  const [orders, setOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(page);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = useCallback(
    async (pageNum: number) => {
      if (!isLoggedIn) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await accountApi.getOrders(pageNum, perPage);
        setOrders(response.items || []);
        setTotal(response.total);
        setPages(response.pages);
        setCurrentPage(pageNum);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Erreur lors du chargement';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoggedIn, perPage]
  );

  useEffect(() => {
    fetchOrders(page);
  }, [fetchOrders, page]);

  const refetch = useCallback(async () => {
    await fetchOrders(currentPage);
  }, [fetchOrders, currentPage]);

  const goToPage = useCallback(
    (pageNum: number) => {
      if (pageNum >= 1 && pageNum <= pages) {
        fetchOrders(pageNum);
      }
    },
    [fetchOrders, pages]
  );

  return {
    orders,
    total,
    pages,
    currentPage,
    isLoading,
    error,
    refetch,
    goToPage,
  };
}

/**
 * Fetch single order by ID.
 */
interface UseOrderResult {
  order: Order | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useOrder(orderId: number | null): UseOrderResult {
  const { isLoggedIn } = useAuth();

  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrder = useCallback(async () => {
    if (!isLoggedIn || !orderId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await accountApi.getOrder(orderId);
      setOrder(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Commande introuvable';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [isLoggedIn, orderId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  return {
    order,
    isLoading,
    error,
    refetch: fetchOrder,
  };
}
